import { format } from "date-fns";
import { debounce } from "lodash";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDrop } from "react-dnd";
import { useDispatch, useSelector } from "react-redux";
import { VariableSizeList } from "react-window";
import AddEntityIcon from "../../../../../components/icons/AddEntityIcon";
import AddRoomInformationIcon from "../../../../../components/icons/AddRoomInformationIcon";
import AddRoomTimeIcon from "../../../../../components/icons/AddRoomTimeIcon";
import DeleteActionIcon from "../../../../../components/icons/DeleteActionIcon";
import EditRoomInformationIcon from "../../../../../components/icons/EditRoomInformationIcon";
import NotificationSentIcon from "../../../../../components/icons/NotificationSentIcon";
import RefreshIcon from "../../../../../components/icons/RefreshIcon";
import ShowAllEntriesIcon from "../../../../../components/icons/ShowAllEntriesIcon";
import TextInput from "../../../../../components/inputs/textInput/TextInput";
import Loader from "../../../../../components/loader/Loader";
import Modal from "../../../../../components/modal/Modal";
import SendNotification from "../../../../../components/sendNotification/SendNotification";
import TextWrapper from "../../../../../components/textWrapper/TextWrapper";
import { showConfirmationModal } from "../../../../../features/confirmationModal/ConfirmationModalSlice";
import { showModal } from "../../../../../features/statusModal/statusModal";
import {
  fetchTablePlayersById,
  movePlayers,
  setTournamentEntryChecked,
} from "../../../../../features/players/playersSlice";
import {
  upsertTableRoomInformation,
  upsertTableRoomTime,
} from "../../../../../features/tables/tableSlice";
import { fetchAvailableTimelinesAPI } from "../../../../../features/tables/tablesAPI";
import {
  fetchUsersByQuery,
  resetSearchResults,
  updateResultsFromCache,
} from "../../../../../features/userSearch/userSearchSlice";
import { getLocalizedTime, getFormattedTimeInZone } from "../../../../../utils/timeZone";
import { fromZonedTime } from "date-fns-tz";
import AddPlayer from "./addPlayer/AddPlayer";
import DisplayAllPlayers from "./displayAllPlayers/DisplayAllPlayers";
import OnePlayer from "./onePlayer/OnePlayer";
import OneSquad from "./oneSquad/OneSquad";
import styles from "./oneTable.module.css";

function utcTimeToLocal(utcTimeStr, tz) {
  if (!utcTimeStr) return utcTimeStr;
  const [h, m] = utcTimeStr.split(":").map(Number);
  const now = new Date();
  const utcDate = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), h, m),
  );
  return getFormattedTimeInZone(utcDate.toISOString(), tz);
}

function utcTimeFallsOnSameLocalDay(utcTimeStr, tz) {
  if (!utcTimeStr) return true;
  const [h, m] = utcTimeStr.split(":").map(Number);
  // Use a fixed reference date (Jan 15) to avoid month-boundary issues
  const utcDate = new Date(Date.UTC(2026, 0, 15, h, m));
  const localDate = getLocalizedTime(utcDate.toISOString(), tz);
  return localDate.getDate() === 15;
}

const ITEM_TYPE = "player";

const SOLO_ROW_HEIGHT = 78;
const SQUAD_ROW_COLLAPSED_HEIGHT = 78;
const SQUAD_MEMBER_ROW_HEIGHT = 56;
const SQUAD_MEMBERS_WRAPPER_HEIGHT = 18;

const tableTypeLabelMap = {
  qualification: "تصفيات",
  nextStage: "متقدمة",
  final: "نهائيات",
  winners: "الفائزين",
};

const getRowId = (row, index) => String(row?.rowId || row?._id || index);

const normalizeRoomInfoValue = (value) => String(value || "").trim();

const normalizeRoomDateTimeValue = (value) => {
  if (!value) return "";
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return "";
  return format(parsedDate, "yyyy-MM-dd'T'HH:mm");
};

const hasRoomInformation = (roomInfo = {}) =>
  Boolean(
    normalizeRoomInfoValue(roomInfo?.roomName) ||
      normalizeRoomInfoValue(roomInfo?.roomId) ||
      normalizeRoomInfoValue(roomInfo?.roomPass) ||
      normalizeRoomInfoValue(roomInfo?.body) ||
      normalizeRoomInfoValue(roomInfo?.enBody),
  );

const VirtualizedRow = ({ index, style, data }) => {
  const row = data.rows[index];
  if (!row) return null;

  const rowId = getRowId(row, index);
  const itemStyle = {
    ...style,
    width: "100%",
    paddingBottom: index === data.rows.length - 1 ? 0 : 7,
    boxSizing: "border-box",
  };

  if (row?.kind === "squad") {
    const isExpanded = data.expandedSquads[rowId] ?? true;

    return (
      <div style={itemStyle}>
        <OneSquad
          squad={row}
          tableId={data.tableId}
          squadIndex={index}
          isSelected={data.selectedIds.has(rowId)}
          isChecked={Boolean(row?.isChecked)}
          isCheckUpdating={data.entryCheckStatusByRowId[rowId] === "loading"}
          showMembers={isExpanded}
          onToggleMembers={() => data.onToggleSquadMembers(rowId, index)}
          onToggleChecked={(nextChecked) =>
            data.onToggleEntryChecked(row, nextChecked)
          }
        />
      </div>
    );
  }

  return (
    <div style={itemStyle}>
      <OnePlayer
        player={row}
        tableId={data.tableId}
        playerIndex={index}
        isSelected={data.selectedIds.has(rowId)}
        isChecked={Boolean(row?.isChecked)}
        isCheckUpdating={data.entryCheckStatusByRowId[rowId] === "loading"}
        onToggleChecked={(nextChecked) =>
          data.onToggleEntryChecked(row, nextChecked)
        }
      />
    </div>
  );
};

const OneTable = ({
  tournamentId,
  tournamentName,
  tableId,
  type,
  timeline,
  query,
  selectedIds,
  gameName,
  tournamentType = "solo",
  setNumberColorTheme = null,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [addPlayerSearchValue, setAddPlayerSearchValue] = useState("");
  const [showAllPlayers, setShowAllPlayers] = useState(false);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [showRoomInfoModal, setShowRoomInfoModal] = useState(false);
  const [savingRoomInfo, setSavingRoomInfo] = useState(false);
  const [showRoomTimeModal, setShowRoomTimeModal] = useState(false);
  const [savingRoomTime, setSavingRoomTime] = useState(false);
  const [roomInfoForm, setRoomInfoForm] = useState({
    roomName: "",
    roomId: "",
    roomPass: "",
    body: "",
    enBody: "",
  });
  const [roomTimeForm, setRoomTimeForm] = useState({
    date: "",
    selectedOptionIndex: 0,
    selectedTimelineIndex: -1,
  });
  const [timelineOptions, setTimelineOptions] = useState([]);
  const [loadingTimelines, setLoadingTimelines] = useState(false);
  const [playersListHeight, setPlayersListHeight] = useState(420);
  const [expandedSquads, setExpandedSquads] = useState({});

  const { timeZone } = useSelector((state) => state.timeZone);
  const { rolePermissions } = useSelector((state) => state.user);
  const { deleteStatus } = useSelector((state) => state.tables);
  const players = useSelector((state) => state.players.data[tableId] || []);
  const entryCheckStatusByRowId = useSelector(
    (state) => state.players.entryCheckStatusByRowId || {},
  );
  const table = useSelector((state) =>
    state.tables.list.find((item) => item._id === tableId),
  );
  const { cache, results, status } = useSelector((state) => state.userSearch);

  const dispatch = useDispatch();
  const debouncedSearch = useRef(null);
  const listRef = useRef(null);
  const playersWrapperRef = useRef(null);

  const searchKind = tournamentType === "squad" ? "squad" : "user";

  const localizedStartDate = getLocalizedTime(timeline?.slotStart, timeZone);
  const localizedEndDate = getLocalizedTime(timeline?.slotEnd, timeZone);
  const localizedRoomCreation = getLocalizedTime(
    timeline?.roomCreation,
    timeZone,
  );
  const localizedRoomEntry = getLocalizedTime(timeline?.roomEntry, timeZone);
  const notificationTime = getLocalizedTime(
    table?.latestNotification?.createdAt,
    timeZone,
  );
  const currentRoomInformation = useMemo(
    () => ({
      roomName: normalizeRoomInfoValue(table?.roomInformation?.roomInfo?.roomName),
      roomId: normalizeRoomInfoValue(table?.roomInformation?.roomInfo?.roomId),
      roomPass: normalizeRoomInfoValue(table?.roomInformation?.roomInfo?.roomPass),
      body: normalizeRoomInfoValue(table?.roomInformation?.roomInfo?.body),
      enBody: normalizeRoomInfoValue(table?.roomInformation?.roomInfo?.enBody),
    }),
    [
      table?.roomInformation?.roomInfo?.roomName,
      table?.roomInformation?.roomInfo?.roomId,
      table?.roomInformation?.roomInfo?.roomPass,
      table?.roomInformation?.roomInfo?.body,
      table?.roomInformation?.roomInfo?.enBody,
    ],
  );
  const tableHasRoomInformation = useMemo(
    () => hasRoomInformation(currentRoomInformation),
    [currentRoomInformation],
  );
  const currentManualRoomTime = useMemo(
    () => normalizeRoomDateTimeValue(table?.roomInformation?.eventAtUtc),
    [table?.roomInformation?.eventAtUtc],
  );
  const tableHasManualRoomTime = Boolean(currentManualRoomTime);
  const isWinnersTable = type === "winners";
  const automaticRoomTimeAlreadySent = Boolean(
    table?.latestNotification?.automatic && notificationTime,
  );

  const [, drop] = useDrop({
    accept: ITEM_TYPE,
    drop: (item) => {
      const fromTableId = item?.tableId;
      if (!fromTableId || fromTableId === tableId) return;

      const move =
        item?.kind === "squad"
          ? {
              kind: "squad",
              squadId: item?.squadId,
              fromTableId,
            }
          : {
              kind: "user",
              userId: item?.userId,
              fromTableId,
            };

      if (
        (move.kind === "squad" && !move.squadId) ||
        (move.kind === "user" && !move.userId)
      ) {
        return;
      }

      dispatch(
        movePlayers({
          entryMoves: [move],
          toTableId: tableId,
          tournamentId,
        }),
      );
    },
  });

  const handleDeleteTable = () => {
    dispatch(
      showConfirmationModal({
        message: "هل انت متاكد من حذف الجدول ؟",
        data: tableId,
        actionType: "deleteTable",
      }),
    );
  };

  const openRoomInfoModal = useCallback(() => {
    setRoomInfoForm({
      roomName: normalizeRoomInfoValue(currentRoomInformation?.roomName),
      roomId: normalizeRoomInfoValue(currentRoomInformation?.roomId),
      roomPass: normalizeRoomInfoValue(currentRoomInformation?.roomPass),
      body: normalizeRoomInfoValue(currentRoomInformation?.body),
      enBody: normalizeRoomInfoValue(currentRoomInformation?.enBody),
    });
    setShowRoomInfoModal(true);
  }, [currentRoomInformation]);

  const closeRoomInfoModal = useCallback(() => {
    if (savingRoomInfo) return;
    setShowRoomInfoModal(false);
  }, [savingRoomInfo]);

  const openRoomTimeModal = useCallback(async () => {
    setRoomTimeForm({
      date: "",
      selectedOptionIndex: 0,
      selectedTimelineIndex: -1,
    });
    setShowRoomTimeModal(true);
    setLoadingTimelines(true);
    try {
      const response = await fetchAvailableTimelinesAPI(tournamentId);
      const options = response?.data?.timelineOptions || [];
      setTimelineOptions(options);

      // Pre-fill from existing timeline if editing
      if (timeline?.slotStart && options.length) {
        // Extract the date in admin timezone
        const localStart = getLocalizedTime(timeline.slotStart, timeZone);
        const y = localStart.getFullYear();
        const m = String(localStart.getMonth() + 1).padStart(2, "0");
        const d = String(localStart.getDate()).padStart(2, "0");
        const existingDate = `${y}-${m}-${d}`;

        // Extract UTC time from the existing timeline to match against slots
        const existingUtc = new Date(timeline.roomCreation);
        const existingRoomCreation =
          String(existingUtc.getUTCHours()).padStart(2, "0") +
          ":" +
          String(existingUtc.getUTCMinutes()).padStart(2, "0");

        let foundOptionIdx = 0;
        let foundSlotIdx = -1;

        for (let oi = 0; oi < options.length; oi++) {
          const slots = options[oi].timelines || [];
          const si = slots.findIndex(
            (slot) => slot.roomCreation === existingRoomCreation,
          );
          if (si !== -1) {
            foundOptionIdx = oi;
            foundSlotIdx = si;
            break;
          }
        }

        setRoomTimeForm({
          date: existingDate,
          selectedOptionIndex: foundOptionIdx,
          selectedTimelineIndex: foundSlotIdx,
        });
      }
    } catch (_error) {
      setTimelineOptions([]);
    } finally {
      setLoadingTimelines(false);
    }
  }, [tournamentId, timeline, timeZone]);

  const closeRoomTimeModal = useCallback(() => {
    if (savingRoomTime) return;
    setShowRoomTimeModal(false);
  }, [savingRoomTime]);

  const handleSaveRoomInformation = useCallback(async () => {
    const normalizedPayload = {
      roomName: normalizeRoomInfoValue(roomInfoForm?.roomName),
      roomId: normalizeRoomInfoValue(roomInfoForm?.roomId),
      roomPass: normalizeRoomInfoValue(roomInfoForm?.roomPass),
      body: normalizeRoomInfoValue(roomInfoForm?.body),
      enBody: normalizeRoomInfoValue(roomInfoForm?.enBody),
    };

    if (
      !normalizedPayload.roomName ||
      !normalizedPayload.roomId ||
      !normalizedPayload.roomPass ||
      !normalizedPayload.body
    ) {
      dispatch(
        showModal({
          message: "يرجى ادخال اسم الروم والايدي والباسوورد ونص الرسالة",
          status: "error",
        }),
      );
      return;
    }

    setSavingRoomInfo(true);
    try {
      await dispatch(
        upsertTableRoomInformation({
          tournamentId,
          tableId,
          roomInfo: normalizedPayload,
        }),
      ).unwrap();
      setShowRoomInfoModal(false);
    } catch (_error) {
      // Thunk already handles and shows the error modal.
    } finally {
      setSavingRoomInfo(false);
    }
  }, [dispatch, roomInfoForm, tableId, tournamentId]);

  const handleSaveRoomTime = useCallback(async () => {
    if (automaticRoomTimeAlreadySent) {
      dispatch(
        showModal({
          message: "تم إرسال اشعار الموعد التلقائي لهذا الجدول مسبقاً",
          status: "error",
        }),
      );
      return;
    }

    if (!roomTimeForm.date) {
      dispatch(
        showModal({
          message: "يرجى اختيار التاريخ",
          status: "error",
        }),
      );
      return;
    }

    const selectedOption = timelineOptions[roomTimeForm.selectedOptionIndex];
    const selectedSlot = selectedOption?.timelines?.[roomTimeForm.selectedTimelineIndex];

    if (!selectedSlot) {
      dispatch(
        showModal({
          message: "يرجى اختيار موعد من المواعيد المتاحة",
          status: "error",
        }),
      );
      return;
    }

    // Convert admin's local date to the correct UTC date.
    // The slot times are UTC but displayed as local, so the admin picks a date
    // in their local context. We combine (local date + local roomCreation time)
    // and convert to UTC to get the correct UTC date.
    const localRoomCreation = utcTimeToLocal(selectedSlot.roomCreation, timeZone);
    const [lh, lm] = localRoomCreation.split(":").map(Number);
    const [year, month, day] = roomTimeForm.date.split("-").map(Number);
    const localDate = new Date(year, month - 1, day, lh, lm, 0);
    const utcDate = fromZonedTime(localDate, timeZone);
    const utcDateStr = utcDate.toISOString().split("T")[0];

    setSavingRoomTime(true);
    try {
      await dispatch(
        upsertTableRoomTime({
          tournamentId,
          tableId,
          date: utcDateStr,
          timelineSlot: selectedSlot,
        }),
      ).unwrap();
      setShowRoomTimeModal(false);
    } catch (_error) {
      // Thunk already handles and shows the error modal.
    } finally {
      setSavingRoomTime(false);
    }
  }, [
    automaticRoomTimeAlreadySent,
    dispatch,
    roomTimeForm.date,
    roomTimeForm.selectedOptionIndex,
    roomTimeForm.selectedTimelineIndex,
    timeZone,
    timelineOptions,
    tableId,
    tournamentId,
  ]);

  const filteredPlayers = useMemo(() => {
    if (!searchValue?.trim()) return players;

    const normalizedQuery = searchValue.toLowerCase();

    return players.filter((entry) => {
      if (entry?.kind === "squad") {
        const squadMatch = [entry?.name, entry?.squadSlug, entry?.region]
          .filter(Boolean)
          .some((value) =>
            String(value).toLowerCase().includes(normalizedQuery),
          );

        const membersMatch = (entry?.members || []).some((member) =>
          [member?.name, member?.gameName, member?.gameId]
            .filter(Boolean)
            .some((value) =>
              String(value).toLowerCase().includes(normalizedQuery),
            ),
        );

        return squadMatch || membersMatch;
      }

      return [entry?.gameId, entry?.gameName, entry?.name]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedQuery));
    });
  }, [players, searchValue]);

  const displayAllRows =
    tournamentType === "squad"
      ? players.flatMap((entry) => entry?.members || [])
      : players;

  const totalSeatCount = players.reduce(
    (total, entry) => total + Math.max(1, Number(entry?.seatCount) || 1),
    0,
  );

  const handleSearch = useCallback(
    (e) => {
      const value = e.target.value;
      setAddPlayerSearchValue(value);

      if (debouncedSearch.current) {
        debouncedSearch.current(value);
      }
    },
    [setAddPlayerSearchValue],
  );

  const getItemSize = useCallback(
    (index) => {
      const row = filteredPlayers[index];
      if (!row) return SOLO_ROW_HEIGHT;

      if (row.kind !== "squad") return SOLO_ROW_HEIGHT;

      const rowId = getRowId(row, index);
      const isExpanded = expandedSquads[rowId] ?? true;

      if (!isExpanded) {
        return SQUAD_ROW_COLLAPSED_HEIGHT;
      }

      const membersCount = Array.isArray(row.members) ? row.members.length : 0;
      return (
        SQUAD_ROW_COLLAPSED_HEIGHT +
        SQUAD_MEMBERS_WRAPPER_HEIGHT +
        membersCount * SQUAD_MEMBER_ROW_HEIGHT
      );
    },
    [filteredPlayers, expandedSquads],
  );

  const handleToggleSquadMembers = useCallback((rowId, index) => {
    setExpandedSquads((prev) => ({
      ...prev,
      [rowId]: !(prev[rowId] ?? true),
    }));

    requestAnimationFrame(() => {
      listRef.current?.resetAfterIndex(index, true);
    });
  }, []);

  const handleToggleEntryChecked = useCallback(
    (entry, nextChecked) => {
      if (!entry) return;

      dispatch(
        setTournamentEntryChecked({
          tournamentId,
          tableId,
          kind: entry?.kind === "squad" ? "squad" : "user",
          userId: entry?.kind === "squad" ? null : entry?.userId || entry?._id,
          squadId: entry?.kind === "squad" ? entry?.squadId || entry?._id : null,
          isChecked: Boolean(nextChecked),
        }),
      );
    },
    [dispatch, tableId, tournamentId],
  );

  const virtualizedRowsData = useMemo(
    () => ({
      rows: filteredPlayers,
      tableId,
      selectedIds,
      expandedSquads,
      entryCheckStatusByRowId,
      onToggleSquadMembers: handleToggleSquadMembers,
      onToggleEntryChecked: handleToggleEntryChecked,
    }),
    [
      filteredPlayers,
      tableId,
      selectedIds,
      expandedSquads,
      entryCheckStatusByRowId,
      handleToggleSquadMembers,
      handleToggleEntryChecked,
    ],
  );

  useEffect(() => {
    debouncedSearch.current = debounce((value) => {
      if (!value) {
        dispatch(resetSearchResults());
        return;
      }

      const cacheKey = `${searchKind}:${value}`;

      if (cache[cacheKey]) {
        dispatch(updateResultsFromCache(cache[cacheKey]));
      } else {
        dispatch(fetchUsersByQuery({ query: value, kind: searchKind }));
      }
    }, 500);

    return () => {
      if (debouncedSearch.current && debouncedSearch.current.cancel) {
        debouncedSearch.current.cancel();
      }
    };
  }, [dispatch, cache, searchKind]);

  useEffect(() => {
    setSearchValue(query);
  }, [query]);

  useEffect(() => {
    const node = playersWrapperRef.current;
    if (!node) return;

    const updateHeight = () => {
      const nextHeight = Math.max(120, Math.floor(node.clientHeight || 0));
      setPlayersListHeight(nextHeight);
    };

    updateHeight();
    requestAnimationFrame(updateHeight);

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(updateHeight);
      observer.observe(node);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  useEffect(() => {
    listRef.current?.resetAfterIndex(0, true);
  }, [filteredPlayers.length, getItemSize]);

  useEffect(() => {
    setExpandedSquads({});
  }, [tableId]);

  return (
    <div
      className={`${
        type === "final"
          ? styles.finalContainer
          : type === "winners"
            ? styles.winnersTable
            : type === "nextStage"
              ? styles.threeDRoom
              : styles.container
      } `}
      ref={drop}
      data-table-id={tableId}
      style={
        setNumberColorTheme
          ? {
              backgroundColor: setNumberColorTheme.backgroundColor,
              borderColor: setNumberColorTheme.borderColor,
            }
          : undefined
      }
    >
      <div className={styles.header}>
        <div className={styles.tableInfo}>
          <div className={styles.regionWrapper}>
            <div className={styles.regionAndNameWrapper}>
              <TextWrapper textStyle={"textMid"}>{table?.name}</TextWrapper>
              <TextWrapper textStyle={"textModal"}>{table?.region}</TextWrapper>
              <TextWrapper textStyle={"textModal"}>
                {`المرحلة: ${tableTypeLabelMap[type] || type || "-"}`}
              </TextWrapper>
            </div>
            <div className={styles.limitWrapper}>
              <div className={styles.playersNumberWrapper}>
                <TextWrapper textStyle={"textSmall"}>
                  {tournamentType === "squad" ? "عدد الفرق" : "عدد اللاعبين"}:{" "}
                  {players?.length}
                </TextWrapper>
              </div>
              <div className={styles.playersNumberWrapper}>
                <TextWrapper textStyle={"textSmall"}>
                  المقاعد: {totalSeatCount}
                </TextWrapper>
              </div>
              <TextWrapper textStyle={"textSmall"}>
                سعة الجدول : {table?.playersLimit}
              </TextWrapper>
            </div>
            <div className={styles.timeWrapper}>
              <TextWrapper textStyle={"textMid"}>
                {timeline?.roomCreation &&
                  `وقت انشاء الروم :
                  ${format(localizedRoomCreation, "aaa h:mm:ss").toUpperCase()}`}
              </TextWrapper>
              <TextWrapper textStyle={"textMid"}>
                {timeline?.roomEntry &&
                  `وقت دخول اللاعبين :
                  ${format(localizedRoomEntry, "aaa h:mm:ss").toUpperCase()}`}
              </TextWrapper>
              <div className={styles.startTimeWrapper}>
                <TextWrapper textStyle={"textMid"}>
                  {timeline?.slotStart &&
                    `وقت بداية الروم :
                  ${format(localizedStartDate, "aaa h:mm:ss").toUpperCase()}`}
                </TextWrapper>
              </div>
              <div className={styles.endTimeWrapper}>
                <TextWrapper textStyle={"textMid"}>
                  {timeline?.slotEnd &&
                    `وقت انتهاء الروم :
                 ${format(localizedEndDate, "aaa h:mm:ss").toUpperCase()}`}
                </TextWrapper>
              </div>
            </div>
          </div>
          <div className={styles.tableOptions} data-no-select-box>
            {rolePermissions.includes("send-table-notification") && (
              <SendNotification
                targetType={"table"}
                type={"table"}
                targetId={tableId}
                contextTournamentId={tournamentId}
                contextTournamentName={tournamentName}
                iconOnly
                tooltip="ارسال اشعار"
                className={styles.tableNotificationAction}
                iconClassName={styles.tableIconImage}
              />
            )}
            {!isWinnersTable && (
              <button
                type="button"
                className={`${styles.showTableAllPlayersWrapper} ${styles.showAllAction}`}
                onClick={() => setShowAllPlayers(true)}
                title={
                  tournamentType === "squad"
                    ? "عرض كل الاعضاء"
                    : "عرض جميع اللاعبين"
                }
              >
                <ShowAllEntriesIcon className={styles.tableIconImage} />
              </button>
            )}
            {!isWinnersTable &&
              rolePermissions.includes("update-tables") &&
              (deleteStatus === "loading" ? (
                <Loader size={20} />
              ) : (
                <button
                  type="button"
                  className={`${styles.deleteTableWrapper} ${styles.deleteAction}`}
                  onClick={handleDeleteTable}
                  title="حذف الجدول"
                >
                  <DeleteActionIcon className={styles.tableIconImage} />
                </button>
              ))}
            {rolePermissions.includes("update-tables") && (
              <button
                type="button"
                className={`${styles.refreshTableWrapper} ${styles.refreshAction}`}
                onClick={() =>
                  dispatch(
                    fetchTablePlayersById({
                      tableId,
                      tournamentId,
                    }),
                  )
                }
                title="تحديث الجدول"
              >
                <RefreshIcon className={styles.tableIconImage} />
              </button>
            )}
            {!isWinnersTable && rolePermissions.includes("update-tables") && (
              <button
                type="button"
                className={`${styles.addPlayerWrapper} ${styles.addEntryAction}`}
                onClick={() => setShowAddPlayer(true)}
                title={tournamentType === "squad" ? "اضافة فريق" : "اضافة لاعب"}
              >
                <AddEntityIcon className={styles.tableIconImage} />
              </button>
            )}
            {!isWinnersTable &&
              rolePermissions.includes("send-table-notification") && (
              <div className={styles.tableOptionsSecondRow}>
                <button
                  type="button"
                  className={`${styles.manageRoomInfoWrapper} ${
                    tableHasRoomInformation
                      ? styles.editRoomInfoAction
                      : styles.addRoomInfoAction
                  }`}
                  onClick={openRoomInfoModal}
                  title={
                    tableHasRoomInformation
                      ? "تعديل معلومات الروم"
                      : "اضافة معلومات الروم"
                  }
                >
                  {tableHasRoomInformation ? (
                    <EditRoomInformationIcon className={styles.tableIconImage} />
                  ) : (
                    <AddRoomInformationIcon className={styles.tableIconImage} />
                  )}
                </button>
                <span
                  className={styles.buttonTooltipWrapper}
                  title={
                    automaticRoomTimeAlreadySent
                      ? "تم إرسال اشعار الموعد التلقائي"
                      : tableHasManualRoomTime
                        ? "تعديل موعد الروم"
                        : "إضافة موعد الروم"
                  }
                >
                  <button
                    type="button"
                    className={`${styles.manageRoomTimeWrapper} ${styles.addRoomTimeAction}`}
                    onClick={openRoomTimeModal}
                    disabled={automaticRoomTimeAlreadySent}
                  >
                    <AddRoomTimeIcon className={styles.tableIconImage} />
                  </button>
                </span>
              </div>
            )}
            {!isWinnersTable &&
              table?.latestNotification?.automatic &&
              notificationTime && (
              <div className={styles.tableOptionsThirdRow}>
                <div
                  className={styles.notificationTimeWrapper}
                  title="تم ارسال اشعار الموعد التلقائي"
                >
                  <NotificationSentIcon className={styles.checkImage} />
                  <span className={styles.notificationTimestamp}>
                    {format(notificationTime, "dd/MM/yyyy | hh:mm aaa").toUpperCase()}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.inputWrapper} data-no-select-box>
        <TextInput
          height="50px"
          width="100%"
          borderRadius="10px"
          bordered
          placeHolder={
            tournamentType === "squad"
              ? "البحث عن فريق أو عضو"
              : "البحث عن لاعب في الجدول"
          }
          bgColor={"grey"}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          data-no-select-box
        />
      </div>

      <div className={styles.playersTitle}>
        <TextWrapper textStyle={"textSmall"}>المعرف</TextWrapper>
        <TextWrapper textStyle={"textSmall"}>
          {tournamentType === "squad" ? "الفرق" : "اللاعبين"}
        </TextWrapper>
      </div>

      <div className={styles.playersWrapper} ref={playersWrapperRef}>
        <VariableSizeList
          ref={listRef}
          height={playersListHeight}
          width="100%"
          itemCount={filteredPlayers.length}
          itemSize={getItemSize}
          itemData={virtualizedRowsData}
          estimatedItemSize={SOLO_ROW_HEIGHT}
          overscanCount={4}
          itemKey={(index, data) => getRowId(data.rows[index], index)}
          className={styles.virtualizedList}
        >
          {VirtualizedRow}
        </VariableSizeList>
      </div>

      {showAllPlayers && (
        <Modal
          closeModalHandler={() => setShowAllPlayers(false)}
          data-no-select-box
        >
          <DisplayAllPlayers allPlayers={displayAllRows} />
        </Modal>
      )}

      {showAddPlayer && (
        <AddPlayer
          onClose={() => setShowAddPlayer(false)}
          value={addPlayerSearchValue}
          onChange={handleSearch}
          loading={status === "loading"}
          players={results}
          gameName={gameName}
          tournamentId={tournamentId}
          tableId={tableId}
          tournamentType={tournamentType}
        />
      )}

      {showRoomInfoModal && (
        <Modal closeModalHandler={closeRoomInfoModal} data-no-select-box>
          <div className={styles.roomInfoModalWrapper}>
            <TextWrapper textStyle={"textMid"}>
              {tableHasRoomInformation
                ? "تعديل معلومات الروم"
                : "إضافة معلومات الروم"}
            </TextWrapper>
            <TextInput
              height="50px"
              width="100%"
              borderRadius="10px"
              bordered
              placeHolder="اسم الروم"
              bgColor={"grey"}
              value={roomInfoForm.roomName}
              onChange={(event) =>
                setRoomInfoForm((prev) => ({
                  ...prev,
                  roomName: event.target.value,
                }))
              }
            />
            <TextInput
              height="50px"
              width="100%"
              borderRadius="10px"
              bordered
              placeHolder="ايدي الروم"
              bgColor={"grey"}
              value={roomInfoForm.roomId}
              onChange={(event) =>
                setRoomInfoForm((prev) => ({
                  ...prev,
                  roomId: event.target.value,
                }))
              }
            />
            <TextInput
              height="50px"
              width="100%"
              borderRadius="10px"
              bordered
              placeHolder="باسوورد الروم"
              bgColor={"grey"}
              value={roomInfoForm.roomPass}
              onChange={(event) =>
                setRoomInfoForm((prev) => ({
                  ...prev,
                  roomPass: event.target.value,
                }))
              }
            />
            <textarea
              className={styles.roomInfoTextArea}
              value={roomInfoForm.body}
              onChange={(event) =>
                setRoomInfoForm((prev) => ({
                  ...prev,
                  body: event.target.value,
                }))
              }
              placeholder="نص الرسالة بالعربية"
              dir="rtl"
            />
            <textarea
              className={styles.roomInfoTextArea}
              value={roomInfoForm.enBody}
              onChange={(event) =>
                setRoomInfoForm((prev) => ({
                  ...prev,
                  enBody: event.target.value,
                }))
              }
              placeholder="Message body in English"
              dir="ltr"
            />

            <div className={styles.roomInfoModalButtons}>
              <button
                type="button"
                className={styles.roomInfoSaveBtn}
                onClick={handleSaveRoomInformation}
                disabled={savingRoomInfo}
              >
                {savingRoomInfo ? <Loader size={18} /> : "حفظ"}
              </button>
              <button
                type="button"
                className={styles.roomInfoCancelBtn}
                onClick={closeRoomInfoModal}
                disabled={savingRoomInfo}
              >
                إلغاء
              </button>
            </div>
          </div>
        </Modal>
      )}

      {showRoomTimeModal && (
        <Modal closeModalHandler={closeRoomTimeModal} data-no-select-box>
          <div className={styles.roomInfoModalWrapper}>
            <TextWrapper textStyle={"textMid"}>
              {tableHasManualRoomTime ? "تعديل موعد الروم" : "إضافة موعد الروم"}
            </TextWrapper>

            <TextWrapper textStyle={"textSmall"}>اختر التاريخ</TextWrapper>
            <input
              type="date"
              className={styles.roomTimeInput}
              value={roomTimeForm.date}
              onChange={(event) =>
                setRoomTimeForm((prev) => ({
                  ...prev,
                  date: event.target.value,
                }))
              }
            />

            {loadingTimelines ? (
              <Loader size={24} />
            ) : timelineOptions.length > 0 ? (
              <>
                {timelineOptions.length > 1 && (
                  <>
                    <TextWrapper textStyle={"textSmall"}>
                      اختر نوع التايملاين
                    </TextWrapper>
                    <div className={styles.timelineOptionTabs}>
                      {timelineOptions.map((option, idx) => (
                        <button
                          key={option.roomTime}
                          type="button"
                          className={`${styles.timelineOptionTab} ${
                            roomTimeForm.selectedOptionIndex === idx
                              ? styles.timelineOptionTabActive
                              : ""
                          }`}
                          onClick={() =>
                            setRoomTimeForm((prev) => ({
                              ...prev,
                              selectedOptionIndex: idx,
                              selectedTimelineIndex: -1,
                            }))
                          }
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}

                <TextWrapper textStyle={"textSmall"}>
                  اختر الموعد
                </TextWrapper>
                <div className={styles.timelineSlotsWrapper}>
                  {(
                    timelineOptions[roomTimeForm.selectedOptionIndex]
                      ?.timelines || []
                  )
                    .map((slot, idx) => ({ slot, originalIndex: idx }))
                    .filter(({ slot }) =>
                      utcTimeFallsOnSameLocalDay(slot.roomCreation, timeZone),
                    )
                    .map(({ slot, originalIndex }) => (
                    <button
                      key={`${slot.roomStart}-${slot.roomEnd}`}
                      type="button"
                      className={`${styles.timelineSlotBtn} ${
                        roomTimeForm.selectedTimelineIndex === originalIndex
                          ? styles.timelineSlotBtnActive
                          : ""
                      }`}
                      onClick={() =>
                        setRoomTimeForm((prev) => ({
                          ...prev,
                          selectedTimelineIndex: originalIndex,
                        }))
                      }
                    >
                      <span className={styles.timelineSlotTime}>
                        {utcTimeToLocal(slot.roomStart, timeZone)}
                      </span>
                      <span className={styles.timelineSlotRange}>
                        {utcTimeToLocal(slot.roomCreation, timeZone)} - {utcTimeToLocal(slot.roomEnd, timeZone)}
                      </span>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <TextWrapper textStyle={"textSmall"}>
                لا توجد مواعيد متاحة
              </TextWrapper>
            )}

            <div className={styles.roomInfoModalButtons}>
              <button
                type="button"
                className={styles.roomInfoSaveBtn}
                onClick={handleSaveRoomTime}
                disabled={savingRoomTime || loadingTimelines}
              >
                {savingRoomTime ? <Loader size={18} /> : "حفظ"}
              </button>
              <button
                type="button"
                className={styles.roomInfoCancelBtn}
                onClick={closeRoomTimeModal}
                disabled={savingRoomTime}
              >
                إلغاء
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default OneTable;
